import { useCurrentUser, useIncidents, useUsers } from "@crm/api";
import { AlertCircle, CheckCircle, Clock, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@crm/utils";
import { useMemo } from "react";

const statusConfig: Record<string, { label: string; color: string }> = {
  new: { label: "New", color: "bg-blue-100 text-blue-800" },
  assigned: { label: "Assigned", color: "bg-yellow-100 text-yellow-800" },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800" },
  on_hold: { label: "On Hold", color: "bg-gray-100 text-gray-800" },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800" },
  closed: { label: "Closed", color: "bg-green-100 text-green-800" },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
};

export function DashboardPage() {
  const { data: user } = useCurrentUser();
  const { data: incidentsData, isLoading: incidentsLoading } = useIncidents({
    page_size: 10,
  });
  const { data: usersData } = useUsers({ role: "technician" });

  // Calculate stats from incidents
  const stats = useMemo(() => {
    const incidents = incidentsData?.results || [];
    const today = new Date().toDateString();

    return {
      open: incidents.filter(
        (i) => i.status === "new" || i.status === "assigned"
      ).length,
      inProgress: incidents.filter((i) => i.status === "in_progress").length,
      resolvedToday: incidents.filter(
        (i) =>
          i.status === "resolved" &&
          new Date(i.updated_at).toDateString() === today
      ).length,
      activeWorkers:
        usersData?.results?.filter((u) => u.is_active && u.role !== "client")
          .length || 0,
    };
  }, [incidentsData, usersData]);

  const recentIncidents = incidentsData?.results?.slice(0, 5) || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Welcome back, {user?.first_name}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Open Incidents"
          value={stats.open}
          icon={AlertCircle}
          color="text-red-500"
        />
        <StatCard
          title="In Progress"
          value={stats.inProgress}
          icon={Clock}
          color="text-yellow-500"
        />
        <StatCard
          title="Resolved Today"
          value={stats.resolvedToday}
          icon={CheckCircle}
          color="text-green-500"
        />
        <StatCard
          title="Active Workers"
          value={stats.activeWorkers}
          icon={Users}
          color="text-blue-500"
        />
      </div>

      {/* Recent Incidents */}
      <div className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Recent Incidents
          </h2>
          <Link
            to="/incidents"
            className="text-sm text-primary hover:underline"
          >
            View all
          </Link>
        </div>
        {incidentsLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : recentIncidents.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              No recent incidents. Create one to get started.
            </p>
            <Link
              to="/incidents/new"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Create Incident
            </Link>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card">
            <div className="divide-y divide-border">
              {recentIncidents.map((incident) => {
                const status =
                  statusConfig[incident.status] || statusConfig.new;
                const priority =
                  priorityConfig[incident.priority] || priorityConfig.medium;
                return (
                  <Link
                    key={incident.id}
                    to={`/incidents/${incident.id}`}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {incident.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {incident.client_company?.name || "No client"}
                        {incident.assigned_to &&
                          ` • Assigned to ${incident.assigned_to.full_name}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                          priority.color
                        )}
                      >
                        {priority.label}
                      </span>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                          status.color
                        )}
                      >
                        {status.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color?: string;
}

function StatCard({
  title,
  value,
  icon: Icon,
  color = "text-muted-foreground",
}: StatCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="mt-1 text-3xl font-semibold text-foreground">{value}</p>
        </div>
        <div className="rounded-md bg-muted p-3">
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </div>
    </div>
  );
}
